import React, { useState, useRef, useEffect } from 'react';
import imageCompression from 'browser-image-compression';
import { Camera, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AvatarUpload = ({ currentAvatar, onAvatarChange, theme }) => {
  const [preview, setPreview] = useState(currentAvatar);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Sync preview when currentAvatar changes externally
  useEffect(() => {
    setPreview(currentAvatar);
  }, [currentAvatar]);

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione uma imagem válida');
      return;
    }

    setUploading(true);

    try {
      // Compress image before upload
      const options = {
        maxSizeMB: 0.3,           // 300KB max
        maxWidthOrHeight: 400,     // 400x400 px
        useWebWorker: true,
        fileType: 'image/jpeg',    // Force JPEG for best compression
        quality: 0.8,
      };

      const compressedFile = await imageCompression(file, options);

      // Upload to R2 via backend
      const formData = new FormData();
      formData.append('file', compressedFile, `avatar.${compressedFile.type === 'image/png' ? 'png' : 'jpg'}`);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/profile/avatar/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const avatarUrl = response.data.avatar_url;
      setPreview(avatarUrl);
      onAvatarChange(avatarUrl);
      toast.success('Foto atualizada com sucesso!');

    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error);
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Erro ao fazer upload. Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/profile/avatar`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreview(null);
      onAvatarChange(null);
      toast.success('Foto removida com sucesso!');
    } catch {
      toast.error('Erro ao remover foto');
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px'
    }}>
      {/* Avatar Preview */}
      <div style={{
        position: 'relative',
        width: '150px',
        height: '150px'
      }}>
        {preview ? (
          <div style={{
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            overflow: 'hidden',
            border: `4px solid ${theme.border}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            <img
              src={preview}
              alt="Avatar"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              referrerPolicy="no-referrer"
            />
          </div>
        ) : (
          <div style={{
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #0DAEFF 0%, #0080CC 100%)',
            border: `4px solid ${theme.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '48px',
            fontWeight: '700',
            color: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            {getInitials('User')}
          </div>
        )}

        {/* Camera button overlay */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          style={{
            position: 'absolute',
            bottom: '8px',
            right: '8px',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: '#0DAEFF',
            border: '3px solid white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: uploading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            opacity: uploading ? 0.6 : 1
          }}
          onMouseEnter={(e) => {
            if (!uploading) {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.background = '#0080CC';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.background = '#0DAEFF';
          }}
        >
          {uploading ? (
            <div style={{
              width: '18px',
              height: '18px',
              border: '2px solid white',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }} />
          ) : (
            <Camera size={18} color="white" />
          )}
        </button>

        {/* Remove button */}
        {preview && !uploading && (
          <button
            onClick={handleRemoveAvatar}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: '#EF4444',
              border: '2px solid white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.background = '#DC2626';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.background = '#EF4444';
            }}
          >
            <X size={16} color="white" />
          </button>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleImageSelect}
        style={{ display: 'none' }}
      />

      {/* Info text */}
      <div style={{
        textAlign: 'center',
        maxWidth: '300px'
      }}>
        <p style={{
          fontSize: '13px',
          color: theme.textSecondary,
          margin: '0 0 8px 0'
        }}>
          Clique no ícone da câmera para adicionar sua foto
        </p>
        <p style={{
          fontSize: '11px',
          color: theme.textSecondary,
          opacity: 0.7,
          margin: 0
        }}>
          Formatos: JPG, PNG, WebP • Tamanho máximo: 10MB
          <br />
          (Será comprimida automaticamente para ~300KB)
        </p>
      </div>

      {/* Animation keyframes */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AvatarUpload;
